/**
 * Admin accounts and scope grants — superadmin only, deliberately outside the RBAC matrix.
 *
 * The invariants this screen exists to make visible:
 *   - **Invitation only.** There is no self-registration path, and a new admin starts with **zero
 *     scopes** — least privilege is the default state, not a policy someone applies.
 *   - **A zero-scope admin is valid**, not broken: they log in and see an empty console.
 *   - **The last superadmin cannot be removed.** Lock-out is impossible by design, and the checkbox
 *     that would do it is disabled with that as its reason.
 *   - **Accountability outlives the account.** Deletion is soft; every audit event they wrote stays,
 *     attributed to them.
 */
import { useEffect, useState } from 'react';
import { Chip, Icon, Kicker, StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { AuditTrailPanel } from '../components/audit';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { ConfirmAction, Consequences, DiffView, FilterBar, ScopeAction, Section } from '../components/kit';
import { SCOPES, SCOPE_OWNS, can } from '../lib/rbac';
import { QUEUE_HINT, useQueue } from '../lib/useQueue';
import { useConsole } from '../app/console';
import {
  useAdmins,
  useDeleteAdmin,
  useInviteAdmin,
  useResetTotp,
  useRevokeAdminSessions,
  useSetActive,
  useSetScopes,
  type AdminRow,
  presetLabel,
  useScopePresets,
} from '../lib/api/hooks/admins';
import { errorMessage } from '../lib/api/client';
import { useSetting } from '../lib/api/hooks/settings';

/** One page is plenty for a staff roster; the footer says the true total either way. */
const PAGE_SIZE = 50;
import {
  ACCESS_RULES,
  DANGER_ACTIONS,
  MATRIX_COLS,
  RBAC_MATRIX,
  SESSION_POLICY,
} from '../fixtures/platform';
import { useUrlSearch } from '../lib/filterCodecs';
import { formatDateTime } from '../lib/datetime';

const VIEWS = [
  { key: 'roster', label: 'Staff roster' },
  { key: 'matrix', label: 'Scope matrix' },
  { key: 'policy', label: 'Session & security policy' },
];

const DETAIL_TABS = ['Scopes', 'Grant history', 'Danger zone'];

/** How many superadmins exist. Removing the last one is refused, here and on the server. */
/**
 * The API row, mapped to what the table renders. A *presentation* mapping only — every value here
 * comes from the response; nothing is invented, defaulted or filled in. `totp_enrolment_status` is
 * a machine string (`enrolled`, `pending`, `reset_required`), and an operator scanning a roster
 * needs a word and a colour, not an enum.
 */
export interface AdminView {
  id: number;
  name: string;
  email: string;
  scopes: readonly string[];
  totp: string;
  tone: 'success' | 'warn' | 'error' | 'neutral';
  active: boolean;
  lastLogin: string;
}

const TOTP_LABEL: Record<string, { label: string; tone: AdminView['tone'] }> = {
  enrolled: { label: 'Enrolled', tone: 'success' },
  pending: { label: 'Pending', tone: 'warn' },
  reset_required: { label: 'Reset required', tone: 'warn' },
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Last sign-in, or — for an invitation nobody has accepted yet — how long it has been waiting
 * (doc 07 §5.4). Past `admin.invite_ttl_hours` the link is dead, and the cell says so rather than
 * leaving a superadmin to wonder why the person never arrived. Never auto-deleted; flagged.
 */
function lastLoginCell(row: AdminRow, now: number, inviteTtlHours?: number): string {
  if (row.last_login_at) return formatDateTime(row.last_login_at);
  if (row.totp_enrolment_status !== 'pending') return 'never';
  const ageMs = now - Date.parse(row.created_at);
  const days = Math.floor(ageMs / DAY_MS);
  const invited = days < 1 ? 'invited today' : `invited ${days} d ago`;
  const expired = inviteTtlHours !== undefined && ageMs > inviteTtlHours * 60 * 60 * 1000;
  return expired ? `never — ${invited}, link expired` : `never — ${invited}`;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What an invitation still needs. The server takes any three characters as an email, so the
 *  console is the only place a typo is caught before a link goes nowhere. */
export function inviteProblems(name: string, email: string): string[] {
  const problems: string[] = [];
  if (name.trim() === '') problems.push('a full name');
  if (!EMAIL.test(email.trim())) problems.push('a work email (name@domain)');
  return problems;
}

/** What `auth.admin_mfa_mode` asks of a new admin (R55). An unread or unknown mode claims nothing. */
export function mfaLine(mode: unknown): string {
  if (mode === 'off') return 'No authenticator code is asked of anyone while auth.admin_mfa_mode is off.';
  if (mode === 'enrolled') return 'An authenticator code is asked only if they choose to enrol one.';
  if (mode === 'required') return 'TOTP enrolment is forced on their first login before they can go anywhere.';
  return 'Whether they must enrol an authenticator follows auth.admin_mfa_mode.';
}

export function toAdminView(row: AdminRow, now: number = Date.now(), inviteTtlHours?: number): AdminView {
  /* An unrecognised status is shown as itself rather than smoothed into a familiar word: a new
     state the console has not been taught yet must look unfamiliar, not fine. */
  const totp = TOTP_LABEL[row.totp_enrolment_status] ?? { label: row.totp_enrolment_status, tone: 'neutral' as const };
  return {
    id: row.id,
    name: row.full_name,
    email: row.email,
    scopes: row.scopes,
    totp: totp.label,
    tone: row.is_active ? totp.tone : 'neutral',
    active: row.is_active,
    lastLogin: lastLoginCell(row, now, inviteTtlHours),
  };
}

const CELL_SKIN = (cell: string) => ({
  background: cell.includes('D') ? 'var(--c-error-bg)' : cell.includes('W') ? 'var(--c-saffron-50)' : 'transparent',
  color: cell === '—' ? 'var(--c-ink3)' : cell.includes('D') ? 'var(--c-error-text)' : 'var(--c-ink)',
});

export function Admins() {
  const { scope, toast } = useConsole();
  const [q, setQ] = useUrlSearch('admins');
  const [view, setView] = useState('roster');
  const [confirm, setConfirm] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState('Scopes');
  const [grants, setGrants] = useState<string[]>([]);

  /* The server filters — `q` is a query parameter, not a client-side `.filter()`. A roster that
     grows past one page would otherwise search only the page you happen to be looking at. */
  /* Superadmin only on the server, so only a superadmin asks; everyone else gets this screen's own
     refusal without a 403 behind it (found by e2e/scopes.spec.ts). */
  const roster = useAdmins({ page: 1, page_size: PAGE_SIZE, q: q.trim() || undefined }, can(scope, 'superadmin'));
  /* The invitation lifetime is a setting, so the dialog, the toast and the expired flag all read it
     rather than a number written into this file. */
  const ttlValue = useSetting(can(scope, 'superadmin') ? 'admin.invite_ttl_hours' : null).data?.setting?.value;
  const inviteTtlHours = typeof ttlValue === 'number' ? ttlValue : undefined;
  const mfaMode = useSetting(can(scope, 'superadmin') ? 'auth.admin_mfa_mode' : null).data?.setting?.value;
  const rows = (roster.data?.items ?? []).map((row) => toAdminView(row, Date.now(), inviteTtlHours));
  const linkValid = inviteTtlHours === undefined ? '' : ` · link valid ${inviteTtlHours} h`;
  const superadmins = rows.filter((a) => a.scopes.includes('superadmin')).length;
  const { focus, setFocus, open, setOpen } = useQueue(rows, { key: q });

  useEffect(() => {
    setGrants(open ? [...open.scopes] : []);
  }, [open]);

  const setScopes = useSetScopes();
  const invite = useInviteAdmin();
  const resetTotp = useResetTotp();
  const revokeSessions = useRevokeAdminSessions();
  const setActive = useSetActive();
  const remove = useDeleteAdmin();
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  /* The presets are a setting, not a fixture; until they load (or if ops empty them) an invitation
     grants nothing, which the dialog already says is allowed. */
  const presets = useScopePresets(can(scope, 'superadmin')).data?.items ?? [];
  const [invitePreset, setInvitePreset] = useState('');
  const chosenPreset = presets.find((p) => presetLabel(p) === invitePreset) ?? presets[0];

  /**
   * Every one of these acts is audited and most of them revoke somebody's sessions, so the failure
   * path matters as much as the success path: the server's refusal is shown verbatim, because the
   * invariants it is protecting — the last superadmin, a superadmin's own authenticator — are its
   * to enforce and its to explain.
   */
  const outcome = (message: string, tone: 'success' | 'info' | 'error' = 'success') => ({
    onSuccess: () => {
      setConfirm(null);
      toast({ tone, message });
    },
    onError: (e: unknown) => {
      setConfirm(null);
      toast({ tone: 'error' as const, message: errorMessage(e) });
    },
  });

  const isSuper = can(scope, 'superadmin');
  const dirty = Boolean(open) && grants.join() !== open?.scopes.join();

  const dialogs = (
    <>
      <ConfirmAction
        open={confirm === 'grant'}
        title="Save scope changes?"
        phrase="SAVE SCOPES"
        tone="error"
        reason
        consequences={[
          'Saving revokes this admin’s sessions; they must log in again with the new access.',
          'The change is audited with the scope that authorised it and your reason.',
          'Nav appears or disappears for them at the next login — there are no half-granted screens.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) =>
          setScopes.mutate(
            { id: open!.id, scopes: grants, reason },
            {
              ...outcome('Scopes saved · sessions revoked'),
              onSuccess: () => {
                setConfirm(null);
                setOpen(null);
                toast({ tone: 'success', message: 'Scopes saved · sessions revoked' });
              },
            },
          )
        }
        confirmLabel="Save scopes"
      />

      <ConfirmAction
        open={confirm === 'invite'}
        title="Invite an admin?"
        phrase="INVITE"
        tone="warn"
        blocked={inviteProblems(inviteName, inviteEmail)}
        consequences={[
          `Sends an invitation link${inviteTtlHours === undefined ? '' : ` valid for ${inviteTtlHours} hours`}; there is no self-registration path.`,
          mfaLine(mfaMode),
          'They start with the scopes you tick — zero is allowed and shows them an empty console.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={() =>
          invite.mutate(
            { full_name: inviteName.trim(), email: inviteEmail.trim(), scopes: chosenPreset?.scopes ?? [] },
            {
              ...outcome(`Invitation sent${linkValid}`),
              onSuccess: () => {
                setConfirm(null);
                setInviteName('');
                setInviteEmail('');
                setInvitePreset('');
                toast({ tone: 'success', message: `Invitation sent${linkValid}` });
              },
            },
          )
        }
        confirmLabel="Send invitation"
      >
        <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--sp-md)' }}>
            <Input density="admin" label="Full name" value={inviteName} onChange={setInviteName} placeholder="Ravi Prakash" />
            <Input density="admin" label="Work email" value={inviteEmail} onChange={setInviteEmail} placeholder="ravi@bookmypandiit.com" />
          </div>
          <Select
            density="admin"
            label="Scope preset"
            options={presets.map(presetLabel)}
            value={chosenPreset ? presetLabel(chosenPreset) : undefined}
            onChange={setInvitePreset}
          />
        </div>
      </ConfirmAction>

      <ConfirmAction
        open={confirm === 'totp'}
        title="Reset this admin’s TOTP?"
        phrase="RESET TOTP"
        tone="error"
        reason
        consequences={[
          'Their authenticator stops working and re-enrolment is forced at the next login.',
          'All sessions are revoked immediately.',
          'You cannot reset your own — break-glass is a runbook, not a button.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) => resetTotp.mutate({ id: open!.id, reason }, outcome('TOTP reset · re-enrolment forced', 'info'))}
        confirmLabel="Reset TOTP"
      />

      <ConfirmAction
        open={confirm === 'sessions'}
        title="Revoke every session?"
        phrase="REVOKE"
        tone="warn"
        reason
        consequences={[
          'Signs this admin out of every browser within seconds.',
          'Their next login needs password and TOTP again.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) => revokeSessions.mutate({ id: open!.id, reason }, outcome('Sessions revoked', 'success'))}
        confirmLabel="Revoke sessions"
      />

      <ConfirmAction
        open={confirm === 'deact'}
        title="Deactivate this admin?"
        phrase="DEACTIVATE"
        tone="error"
        reason
        consequences={[
          'Login is blocked immediately and sessions are revoked.',
          'Their audit trail, decisions and grants stay exactly as they are.',
          'Reactivation is a separate, audited act.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) =>
          setActive.mutate(
            { id: open!.id, is_active: false, reason },
            {
              ...outcome('Account deactivated', 'info'),
              onSuccess: () => {
                setConfirm(null);
                setOpen(null);
                toast({ tone: 'error', message: 'Account deactivated' });
              },
            },
          )
        }
        confirmLabel="Deactivate"
      />

      <ConfirmAction
        open={confirm === 'del'}
        title="Delete this admin account?"
        /* Their own email, so a deletion cannot land on the wrong person. */
        phrase={open?.email ?? 'email'}
        tone="error"
        reason
        consequences={[
          'Soft delete: login is impossible and the row is hidden from the roster.',
          'Every audit event they wrote remains, attributed to them — accountability outlives the account.',
          'The last superadmin can never be deleted.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) =>
          remove.mutate(
            { id: open!.id, reason },
            {
              ...outcome('Account deleted · audit trail retained', 'info'),
              onSuccess: () => {
                setConfirm(null);
                setOpen(null);
                toast({ tone: 'error', message: 'Account deleted · audit trail retained' });
              },
            },
          )
        }
        confirmLabel="Delete account"
      />
    </>
  );

  if (open && isSuper && view === 'roster') {
    return (
      <DetailPage
        title={open.name}
        code={open.email}
        status={<StatusChip tone={open.tone} label={open.totp} />}
        tabs={DETAIL_TABS}
        activeTab={detailTab}
        onTab={setDetailTab}
        onClose={() => setOpen(null)}
        actions={
          <ScopeAction scope={scope} need="superadmin" size="sm" disabled={!dirty} onClick={() => setConfirm('grant')}>
            Save scopes
          </ScopeAction>
        }
      >
        {detailTab === 'Scopes' && (
          <>
            <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
              <Kicker>Seven scopes · switch what this person owns</Kicker>
              {SCOPES.map((s) => {
                const on = grants.includes(s);
                const lastSuper = s === 'superadmin' && on && superadmins === 1;
                return (
                  <label
                    key={s}
                    style={{
                      display: 'flex',
                      gap: 'var(--sp-sm)',
                      alignItems: 'flex-start',
                      padding: 'var(--sp-sm) var(--sp-md)',
                      border: `1px solid ${on ? 'var(--c-saffron)' : 'var(--c-border)'}`,
                      background: on ? 'var(--c-saffron-50)' : 'var(--c-surface2)',
                      borderRadius: 'var(--r-tile)',
                      cursor: lastSuper ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={lastSuper}
                      aria-label={s}
                      onChange={() => setGrants((g) => (on ? g.filter((x) => x !== s) : [...g, s]))}
                      style={{ width: 16, height: 16, marginTop: 3 }}
                    />
                    <span style={{ display: 'grid', gap: 2 }}>
                      <span className="mono-num" style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink)' }}>
                        {s}
                      </span>
                      <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                        {SCOPE_OWNS[s]}
                      </span>
                      {lastSuper && (
                        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-error-text)' }}>
                          Cannot remove the last superadmin — lock-out is impossible by design.
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>

            {dirty && (
              <DiffView
                labels={['Now', 'After saving']}
                rows={[['scopes', open.scopes.join(', ') || 'none', grants.join(', ') || 'none']]}
              />
            )}

            {grants.length === 0 && (
              <Consequences
                tone="warn"
                items={[
                  'With zero scopes this admin can log in and see the shell, and nothing else. That is a valid state, not a bug.',
                ]}
              />
            )}
          </>
        )}

        {detailTab === 'Grant history' && <AuditTrailPanel entityType="admin_user" entityId={open.id} />}

        {detailTab === 'Danger zone' && (
          <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
            {DANGER_ACTIONS.map((a) => (
              <div
                key={a.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-sm)',
                  padding: 'var(--sp-md)',
                  border: '1px solid var(--c-border)',
                  borderRadius: 'var(--r-tile)',
                  background: 'var(--c-surface2)',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: 1, display: 'grid', minWidth: 200 }}>
                  <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink)' }}>{a.label}</span>
                  <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{a.note}</span>
                </div>
                <ScopeAction
                  scope={scope}
                  need="superadmin"
                  size="sm"
                  variant={a.key === 'del' ? 'destructive' : 'secondary'}
                  onClick={() => setConfirm(a.key)}
                >
                  {a.label}
                </ScopeAction>
              </div>
            ))}
            <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
              Every action here is recorded in the audit log under auth_admin and alerts the founder
              channel.
            </span>
          </div>
        )}
        {dialogs}
      </DetailPage>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
          background: isSuper ? 'var(--c-sunk)' : 'var(--c-error-bg)',
          border: `1px solid ${isSuper ? 'var(--c-border)' : 'var(--c-error)'}`,
          color: isSuper ? 'var(--c-ink2)' : 'var(--c-error-text)',
          borderRadius: 'var(--r-tile)',
          padding: 'var(--sp-md) var(--sp-lg)',
          fontSize: 'var(--fs-admin-ui)',
          flexWrap: 'wrap',
        }}
      >
        <Icon name={isSuper ? 'shield-check' : 'lock'} size={16} />
        <span style={{ flex: 1, minWidth: 280 }}>
          {isSuper
            ? 'Admin accounts are invitation-only — no self-registration path exists. A new admin starts with zero scopes; least privilege is the default, not a policy.'
            : 'Requires scope: superadmin. Admin and scope management sits deliberately outside the matrix — ask a superadmin.'}
        </span>
        <ScopeAction scope={scope} need="superadmin" size="sm" icon="user-plus" onClick={() => setConfirm('invite')}>
          Invite admin
        </ScopeAction>
      </div>

      {isSuper && (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
          {VIEWS.map((v) => (
            <Chip key={v.key} variant="filter" label={v.label} selected={view === v.key} onClick={() => setView(v.key)} />
          ))}
        </div>
      )}

      {isSuper && view === 'roster' && (
        <>
          <FilterBar
            q={q}
            onQ={setQ}
            placeholder="Name, email, scope — press /"
            right={
              <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                Granting or revoking a scope revokes that admin’s sessions immediately
              </span>
            }
          />
          <DataTable
            density="comfortable"
            focusedIndex={focus}
            onFocusRow={setFocus}
            onRowClick={(row) => setOpen(rows.find((a) => String(a.id) === String(row.id))!)}
            emptyLabel="No admin matches this search"
            footer={
              <>
                <span>
                  {roster.data?.total ?? 0} admin accounts · {superadmins} superadmin on this page
                </span>
                <span>{QUEUE_HINT}</span>
              </>
            }
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'email', label: 'Email', mono: true },
              { key: 'scopes', label: 'Scopes' },
              { key: 'totp', label: 'TOTP' },
              { key: 'active', label: 'Active' },
              { key: 'last', label: 'Last login', align: 'right' },
            ]}
            rows={rows.map((a) => ({
              id: a.id,
              name: a.name,
              email: a.email,
              scopes:
                a.scopes.length > 0 ? (
                  <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {a.scopes.map((s) =>
                      s === 'superadmin' ? (
                        <StatusChip key={s} tone="error" label="superadmin" icon="shield" />
                      ) : (
                        <Chip key={s} variant="display" label={s} />
                      ),
                    )}
                  </span>
                ) : (
                  <StatusChip tone="warn" label="no modules granted" />
                ),
              totp: <StatusChip tone={a.tone} label={a.totp} />,
              active: a.active ? <StatusChip tone="success" label="Active" /> : <StatusChip label="Deactivated" />,
              last: a.lastLogin,
            }))}
          />
        </>
      )}

      {isSuper && view === 'matrix' && (
        <Section
          title="Scope → router → R / W / D"
          note="The normative matrix · a feature screen may narrow a cell, never widen it"
          pad={false}
        >
          <div style={{ padding: 'var(--sp-lg)', overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--fs-admin-cell)' }}>
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 10px',
                      color: 'var(--c-ink3)',
                      fontWeight: 'var(--fw-regular)',
                      fontSize: 'var(--fs-admin-colhead)',
                      textTransform: 'uppercase',
                      letterSpacing: 'var(--tracking-kicker)',
                    }}
                  >
                    Admin router
                  </th>
                  {MATRIX_COLS.map((c) => (
                    <th
                      key={c}
                      className="mono-num"
                      style={{
                        textAlign: 'center',
                        padding: '6px 10px',
                        color: 'var(--c-ink3)',
                        fontWeight: 'var(--fw-regular)',
                        fontSize: 'var(--fs-admin-colhead)',
                      }}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RBAC_MATRIX.map((row) => (
                  <tr key={row[0]}>
                    <td className="mono-num" style={{ padding: '7px 10px', borderTop: '1px solid var(--c-border)', color: 'var(--c-ink)' }}>
                      {row[0]}
                    </td>
                    {row.slice(1).map((cell, i) => (
                      <td
                        key={MATRIX_COLS[i]}
                        aria-label={`${row[0]} × ${MATRIX_COLS[i]}`}
                        style={{
                          padding: '7px 10px',
                          borderTop: '1px solid var(--c-border)',
                          textAlign: 'center',
                          fontFamily: 'var(--font-mono)',
                          ...CELL_SKIN(cell),
                        }}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '0 var(--sp-lg) var(--sp-lg)', display: 'grid', gap: 6, fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
            <span>
              R read · W create and update · D destructive — delete, void, release, suspend.
              superadmin holds every cell and is omitted; <span className="mono-num">admins_admin</span>{' '}
              is superadmin-only, deliberately outside the matrix.
            </span>
            <span>
              Every D cell means a confirm dialog with consequence copy, a mandatory reason, and an
              audit event. Support may initiate a refund into the finance queue but never executes
              it; support sees KYC status, never documents.
            </span>
          </div>
        </Section>
      )}

      {isSuper && view === 'policy' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 'var(--sp-lg)', alignItems: 'start' }}>
          <Section title="Sessions and tokens" note="Server-enforced — the console only reflects it">
            <KV rows={SESSION_POLICY.map(([k, v]) => [k, v])} />
          </Section>
          <Section title="Access rules" note="The invariants that make lock-out and privilege creep impossible">
            <KV rows={ACCESS_RULES.map(([k, v]) => [k, v])} />
          </Section>
        </div>
      )}
      {dialogs}
    </>
  );
}
